import { useEffect, useMemo, useRef, useState } from 'react';
import {
  extractDocumentDescription,
  extractDocumentTitle,
  resolveMessageSourceContent,
} from '../../../utils/contentUtils.js';
import { getDocumentByTenantUuid } from '../../../services/index.js';

/**
 * @param {object} p
 * @param {import('axios').AxiosInstance|null|undefined} p.apiClient
 * @param {object[]} p.messages
 * @param {object[]} p.sortedDocuments
 * @param {Record<string, string[]>} p.documentCreatedMessageIdsByConversation
 * @param {string|null|undefined} p.conversationId
 * @param {string|null|undefined} p.selectedConversationId
 */
export function useAssistantDocumentThreadMeta({
  apiClient,
  messages,
  sortedDocuments,
  documentCreatedMessageIdsByConversation,
  conversationId,
  selectedConversationId,
}) {
  const [documentDescriptionsByUuid, setDocumentDescriptionsByUuid] = useState({});
  const documentDescriptionCacheRef = useRef(new Map());
  const pendingDocumentDescriptionByUuidRef = useRef(new Map());

  const currentConversationId = conversationId || selectedConversationId;

  useEffect(() => {
    documentDescriptionCacheRef.current.clear();
    pendingDocumentDescriptionByUuidRef.current.clear();
    setDocumentDescriptionsByUuid({});
  }, [currentConversationId]);

  const documentCreatedMessageIds = useMemo(
    () =>
      (currentConversationId && documentCreatedMessageIdsByConversation[currentConversationId]) || [],
    [currentConversationId, documentCreatedMessageIdsByConversation],
  );

  const documentByMessageId = useMemo(() => {
    if (!currentConversationId) return {};

    return (sortedDocuments || []).reduce((acc, doc) => {
      if (!doc?.messageId) return acc;
      if (doc.conversationId !== currentConversationId) return acc;
      acc[doc.messageId] = doc;
      return acc;
    }, {});
  }, [sortedDocuments, currentConversationId]);

  const documentCardTargetUuids = useMemo(() => {
    return documentCreatedMessageIds
      .map((messageId) => documentByMessageId[messageId]?.documentUuid)
      .filter(Boolean);
  }, [documentCreatedMessageIds, documentByMessageId]);

  useEffect(() => {
    if (!apiClient || documentCardTargetUuids.length === 0) return undefined;

    let isCancelled = false;
    const uniqueUuids = Array.from(new Set(documentCardTargetUuids));
    const resolveDescriptionFromResponse = (response) => {
      const body = response?.data?.body ?? response?.body ?? response?.data ?? null;
      const description = body?.description;
      const summaryText = body?.summary?.text;

      if (typeof summaryText === 'string' && summaryText.trim()) return summaryText.trim();
      if (typeof description === 'string' && description.trim()) return description.trim();
      return null;
    };

    const fetchDescriptionByUuid = async (documentUuid) => {
      if (documentDescriptionCacheRef.current.has(documentUuid)) return;

      let pendingRequest = pendingDocumentDescriptionByUuidRef.current.get(documentUuid);
      if (!pendingRequest) {
        pendingRequest = getDocumentByTenantUuid(apiClient, documentUuid)
          .then((response) => {
            const nextDescription = resolveDescriptionFromResponse(response);
            documentDescriptionCacheRef.current.set(documentUuid, nextDescription);
            return nextDescription;
          })
          .catch((error) => {
            console.error('Failed to fetch document description:', error);
            documentDescriptionCacheRef.current.set(documentUuid, null);
            return null;
          })
          .finally(() => {
            pendingDocumentDescriptionByUuidRef.current.delete(documentUuid);
          });

        pendingDocumentDescriptionByUuidRef.current.set(documentUuid, pendingRequest);
      }

      await pendingRequest;
    };

    void Promise.all(uniqueUuids.map((documentUuid) => fetchDescriptionByUuid(documentUuid))).then(() => {
      if (isCancelled) return;

      setDocumentDescriptionsByUuid((previousState) => {
        const nextState = { ...previousState };
        let didChange = false;

        uniqueUuids.forEach((documentUuid) => {
          const cachedValue =
            documentDescriptionCacheRef.current.get(documentUuid) ?? null;
          if (nextState[documentUuid] === cachedValue) return;
          nextState[documentUuid] = cachedValue;
          didChange = true;
        });

        return didChange ? nextState : previousState;
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [apiClient, documentCardTargetUuids]);

  const documentCardMetaByMessageId = useMemo(() => {
    const messageById = (messages || []).reduce((acc, message) => {
      const key = message?.id || message?.messageUuid;
      if (!key) return acc;
      acc[key] = message;
      return acc;
    }, {});

    return documentCreatedMessageIds.reduce((acc, messageId) => {
      const message = messageById[messageId];
      if (!message) return acc;

      const rawContent = resolveMessageSourceContent(message);
      const fallbackTitle = extractDocumentTitle(rawContent);
      const fallbackDescription = extractDocumentDescription(rawContent);
      const documentUuid = documentByMessageId[messageId]?.documentUuid;
      const endpointDescription = documentUuid ? documentDescriptionsByUuid[documentUuid] : null;

      acc[messageId] = {
        title: fallbackTitle || 'Generated document',
        description: endpointDescription ?? fallbackDescription,
      };
      return acc;
    }, {});
  }, [
    messages,
    documentCreatedMessageIds,
    documentByMessageId,
    documentDescriptionsByUuid,
  ]);

  return {
    documentCreatedMessageIds,
    documentCardMetaByMessageId,
  };
}
